import React, { FunctionComponent, useState, useMemo } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close, chevronForward, arrowBack, searchOutline, closeOutline } from 'ionicons/icons';
import { MetaLibrarySubSystemModel, MetaLibraryConstituentModel } from '../models/meta-library.model';

interface ConstituentsModalProps {
  isOpen: boolean;
  subSystem: MetaLibrarySubSystemModel | null;
  systemCode: string;
  onClose: () => void;
  onBack: () => void;
  onConstituentSelect?: (constituent: MetaLibraryConstituentModel, constituentCode: string) => void;
}

export const ConstituentsModal: FunctionComponent<ConstituentsModalProps> = ({
  isOpen,
  subSystem,
  systemCode,
  onClose,
  onBack,
  onConstituentSelect,
}) => {
  const [searchText, setSearchText] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const handleConstituentClick = (constituent: MetaLibraryConstituentModel) => {
    if (onConstituentSelect && subSystem) {
      const constituentCode = `${systemCode}${subSystem.code}${constituent.code}`;
      onConstituentSelect(constituent, constituentCode);
    }
  };

  const handleSearchChange = (e: CustomEvent) => {
    const value = e.detail.value!;
    setSearchText(value);
  };

  const clearSearch = () => {
    setSearchText('');
  };

  // Filtrer les constituants en fonction de la recherche
  const filteredConstituents = useMemo(() => {
    if (!subSystem) return [];
    
    if (!searchText.trim()) {
      return subSystem.metaLibraryConstituents;
    }

    const searchLower = searchText.toLowerCase();
    return subSystem.metaLibraryConstituents.filter(constituent => {
      const fullCode = `${systemCode}${subSystem.code}${constituent.code}`;
      return fullCode.toLowerCase().includes(searchLower) ||
             constituent.constituent.toLowerCase().includes(searchLower);
    });
  }, [subSystem, searchText, systemCode]);

  return (
    <IonModal isOpen={isOpen}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onBack}>
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>Constituants</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {subSystem && (
          <>
            {/* Champ de recherche */}
            <div style={{ marginBottom: '16px' }}>
              <IonItem
                lines="none"
                style={{
                  width: '100%',
                  background: '#F7F8FB',
                  border: '1px solid #ccc',
                  borderRadius: '100px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  alignItems: 'center',
                  height: '40px',
                  display: 'flex',
                }}
              >
                <IonIcon
                  icon={searchOutline}
                  slot="start"
                  style={{
                    fontSize: '16px',
                    color: !searchText ? '#888' : '#fff',
                    backgroundColor: !searchText
                      ? 'transparent'
                      : isSearchFocused
                        ? '#165BE6'
                        : '#4B4D55',
                    borderRadius: '50%',
                    padding: '4px',
                    height: '24px',
                    width: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.3s',
                    marginRight: '6px',
                    verticalAlign: 'middle',
                  }}
                />

                <IonInput
                  placeholder="Rechercher un constituant"
                  value={searchText}
                  onIonInput={handleSearchChange}
                  onIonFocus={() => setIsSearchFocused(true)}
                  onIonBlur={() => setIsSearchFocused(false)}
                  style={{
                    font: 'open-sans, sans-serif',
                    weight: '400',
                    background: 'transparent',
                    flex: 1,
                    fontSize: '12px',
                    lineHeight: '100%',
                    verticalAlign: 'middle',
                    height: '100%',
                    alignItems: 'center',
                  }}
                  clearInput={false}
                  role="search"
                  enterkeyhint="search"
                />

                {searchText && (
                  <IonButton
                    fill="clear"
                    size="small"
                    onClick={clearSearch}
                    style={{
                      padding: '2px',
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      verticalAlign: 'middle',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IonIcon
                      icon={closeOutline}
                      slot="icon-only"
                      style={{
                        fontSize: '14px',
                        color: '#888',
                        backgroundColor: 'transparent',
                      }}
                    />
                  </IonButton>
                )}
              </IonItem>
            </div>

            <IonList>
              {filteredConstituents.map((constituent) => (
                <IonItem
                  key={constituent.constituentTypeId}
                  button
                  onClick={() => handleConstituentClick(constituent)}
                  detail={true}
                  detailIcon={chevronForward}
                >
                  <IonLabel>
                    <h2>
                      {systemCode}{subSystem.code}{constituent.code} - {constituent.constituent}
                    </h2>
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

